import React, { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TESTIMONIALS = [
  { id: "1", name: "Sarah Mitchell", role: "Professional Actor, London" },
  { id: "2", name: "Marcus Thompson", role: "Theatre Director" },
  { id: "3", name: "Amara Okafor", role: "Actor & Movement Coach" },
  { id: "4", name: "James Chen", role: "Screen Actor, 12 Years" },
  { id: "5", name: "Elena Rossi", role: "GPS Method Graduate" },
];

function VideoCard({ testimonial }) {
  const [status, setStatus] = useState("idle");
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [error, setError] = useState(null);

  const generate = useCallback(async () => {
    setStatus("generating");
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/heygen/generate/${testimonial.id}`, { method: "POST" });
      const data = await resp.json();

      if (data.video_url) {
        setVideoUrl(data.video_url);
        setStatus("completed");
        return;
      }

      if (data.video_id) {
        setVideoId(data.video_id);
        setStatus("processing");
      } else {
        throw new Error(data.detail || "Generation failed");
      }
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  }, [testimonial.id]);

  // Poll for status
  useEffect(() => {
    if (status !== "processing" || !videoId) return;

    const interval = setInterval(async () => {
      try {
        const resp = await fetch(`${API_URL}/api/heygen/status/${videoId}`);
        const data = await resp.json();

        if (data.status === "completed" && data.video_url) {
          setVideoUrl(data.video_url);
          setStatus("completed");
          clearInterval(interval);
        } else if (data.status === "failed") {
          setError("Video generation failed");
          setStatus("error");
          clearInterval(interval);
        }
      } catch (e) {
        console.error("Poll error:", e);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [status, videoId]);

  return (
    <div style={{ paddingBottom: 30, borderBottom: "1px solid rgba(217,251,6,0.1)" }}>
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 13, color: "#d9fb06", letterSpacing: "0.15em", marginBottom: 8 }}>
        TESTIMONIAL {testimonial.id.padStart(2, "0")}
      </div>
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 600, fontSize: 20, color: "#dfddd6", marginBottom: 4 }}>
        {testimonial.name}
      </div>
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 14, color: "#888680", marginBottom: 16 }}>
        {testimonial.role}
      </div>

      {/* Video player or placeholder */}
      <div style={{ width: 270, height: 480, background: "#302f2c", border: "1px solid rgba(217,251,6,0.15)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", marginBottom: 12 }}>
        {status === "completed" && videoUrl ? (
          <video
            src={videoUrl}
            controls
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            data-testid={`video-player-${testimonial.id}`}
          />
        ) : status === "processing" ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <div style={{ width: 40, height: 40, border: "3px solid rgba(217,251,6,0.2)", borderTopColor: "#d9fb06", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
            <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 600, fontSize: 14, color: "#d9fb06", marginBottom: 8 }}>GENERATING...</div>
            <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 12, color: "#888680" }}>HeyGen is creating your video.<br />This takes 2-5 minutes.</div>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 20 }}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ margin: "0 auto 12px", display: "block" }}>
              <circle cx="24" cy="24" r="20" stroke="#d9fb06" strokeWidth="1.5" opacity="0.3" />
              <polygon points="20,16 34,24 20,32" fill="#d9fb06" opacity="0.6" />
            </svg>
            <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 13, color: "#888680" }}>Click generate to create<br />a talking head video</div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-2">
        {status !== "completed" && (
          <button
            data-testid={`generate-video-${testimonial.id}`}
            onClick={generate}
            disabled={status === "generating" || status === "processing"}
            style={{
              background: "#d9fb06",
              color: "#1a1c1b",
              border: "none",
              padding: "8px 18px",
              fontFamily: "Inter, Arial, sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: status === "generating" || status === "processing" ? "wait" : "pointer",
              opacity: status === "generating" || status === "processing" ? 0.5 : 1,
            }}
          >
            {status === "generating" ? "Starting..." : status === "processing" ? "Processing..." : "Generate Video"}
          </button>
        )}
        {status === "completed" && videoUrl && (
          <a
            href={videoUrl}
            download={`gps-testimonial-${testimonial.name.toLowerCase().replace(/\s+/g, "-")}.mp4`}
            data-testid={`download-video-${testimonial.id}`}
            style={{
              background: "#d9fb06",
              color: "#1a1c1b",
              border: "none",
              padding: "8px 18px",
              fontFamily: "Inter, Arial, sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            Download Video
          </a>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#ff6b6b" }}>{error}</div>
      )}
    </div>
  );
}

export default function SynthReviews() {
  return (
    <div
      data-testid="synth-reviews-page"
      style={{ background: "#1a1c1b", minHeight: "100vh", padding: "120px 40px 80px" }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ maxWidth: 1200, margin: "0 auto 60px" }}>
        <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 14, color: "#888680", letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>
          AI Video Studio
        </div>
        <h1 className="brand-display" style={{ fontSize: "clamp(2.5rem, 4vw, 5rem)", marginBottom: 20 }}>
          SYNTH REVIEWS
        </h1>
        <p style={{ color: "#888680", fontSize: 18, maxWidth: 700, lineHeight: 1.6 }}>
          AI-generated talking head videos powered by HeyGen. Real faces, real lip-sync, real impact.
          Click generate to create each video — takes about 2-5 minutes per video.
        </p>
        <div style={{ marginTop: 16, padding: "12px 20px", background: "#302f2c", display: "inline-block", fontSize: 14, color: "#dfddd6" }}>
          Format: 9:16 vertical (1080x1920) • Powered by HeyGen AI • Auto lip-sync
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 40 }}>
          {TESTIMONIALS.map((t) => (
            <VideoCard key={t.id} testimonial={t} />
          ))}
        </div>

        {/* Instructions */}
        <div style={{ marginTop: 80, padding: 40, background: "#302f2c" }}>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 16, color: "#d9fb06", marginBottom: 16 }}>
            HOW IT WORKS
          </div>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 15, color: "#dfddd6", lineHeight: 1.8 }}>
            1. Click "Generate Video" on any testimonial<br />
            2. HeyGen AI creates a realistic talking head video (2-5 min)<br />
            3. The video auto-plays when ready — preview it right here<br />
            4. Click "Download Video" to save and post to Instagram<br /><br />
            <span style={{ color: "#888680" }}>
              Each video features a HeyGen AI avatar with lip-synced speech,
              natural expressions, and head movement. Perfect for Instagram Reels.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
